import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { get, map } from 'lodash';
import { JsonLdScript } from 'components/atoms';
import Crumb from './Crumb';

import './BreadCrumbsV2.scss';

function buildHref(rawHref, rootUrl) {
	if (!rawHref) return rootUrl;
	if (/^https?:\/\//i.test(rawHref)) return rawHref;
	const path = rawHref.startsWith('/') ? rawHref : `/${rawHref}`;
	return `${rootUrl.replace(/\/$/, '')}${path}`;
}

function createCLPBreadcrumbSchema(
	baseUrl = 'https://www.selfstorage.com',
	crumbs = [],
) {
	const ROOT_URL = baseUrl;
	const ensureTrailingSlash = (url) => {
		if (!url) return url;
		return url.endsWith('/') ? url : `${url}/`;
	};
	const filtered = crumbs.filter((crumb) => crumb?.props?.label);

	const itemListElement = filtered.map((crumb, index) => {
		const rawHref = (get(crumb, 'props.href', '') || '').split('?').shift();
		const absoluteUrl = buildHref(rawHref, ROOT_URL);
		const idUrl = ensureTrailingSlash(absoluteUrl);

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@id': idUrl,
				name: crumb.props.name || crumb.props.label,
				url: idUrl,
			},
		};
	});

	const cityCrumb = filtered.find(
		(c) => c?.props?.className === 'sublocality',
	);
	let landingUrl = '';

	if (cityCrumb) {
		const raw = (get(cityCrumb, 'props.href', '') || '').split('?').shift();
		landingUrl = ensureTrailingSlash(buildHref(raw, ROOT_URL));
	} else {
		landingUrl =
			get(
				itemListElement,
				`[${itemListElement.length - 1}].item['@id']`,
				'',
			) || ensureTrailingSlash(ROOT_URL);
	}

	const breadcrumbListId = `${landingUrl.replace(/#.*$/, '')}#breadcrumb`;
	return {
		'@type': 'BreadcrumbList',
		'@id': breadcrumbListId,
		itemListElement,
	};
}

function createCrumbSchema(crumbs) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: crumb.props.name || crumb.props.label,
				item: get(crumb, 'props.id', get(crumb, 'props.to', ''))
					.split('?')
					.shift(),
			})),
	};
}

export function BreadCrumbsV2({
	location = {},
	baseUrl: baseUrlProp = 'https://www.selfstorage.com',
	crumbs: crumbsProp = [],
	cityUrl,
	stateUrl,
	tertiary,
	isCLPPage,
	className,
}) {
	const storeBaseUrl = useSelector(
		(state) => state?.app?.baseUrl ?? undefined,
	);
	const baseUrl = storeBaseUrl || baseUrlProp;

	const { fullState, subLocality, city } = location;
	const sub = subLocality || city;

	const normalizedBase = (baseUrl || '').replace(/\/$/, '');
	const joinUrl = (base, path) =>
		`${(base || '').replace(/\/$/, '')}/${(path || '').replace(/^\//, '')}`;

	const newCrumbs = [];

	newCrumbs.push(
		<Crumb
			href={normalizedBase + '/'}
			id={normalizedBase + '/'}
			key="base"
			label="Home"
			name="Home"
			baseUrl={baseUrl}
			tertiary={tertiary}
		/>,
	);

	if (stateUrl) {
		newCrumbs.push(
			<Crumb
				key="state"
				href={joinUrl(normalizedBase, stateUrl)}
				id={joinUrl(normalizedBase, stateUrl)}
				isLast={!cityUrl && !crumbsProp.length}
				label={`${fullState} Self Storage`}
				name={`${fullState} Self Storage`}
				className="state"
				tertiary={tertiary}
			/>,
		);
	}

	if (cityUrl) {
		const isLast = !crumbsProp.length;
		newCrumbs.push(
			<Crumb
				key="sublocality"
				className="sublocality"
				href={joinUrl(normalizedBase, cityUrl)}
				isLast={isLast}
				label={`${sub} Storage`}
				name={sub}
			>
				{sub}
			</Crumb>,
		);
	}

	const crumbs = [...newCrumbs, ...crumbsProp];
	const crumbJSON = isCLPPage
		? createCLPBreadcrumbSchema(baseUrl, crumbs)
		: createCrumbSchema(crumbs);

	return (
		<Fragment>
			<ul
				className={classnames('ss-breadcrumbs-v2', className)}
				id="breadcrumb"
			>
				{map(crumbs, (crumb) => (
					<li
						className="ss-breadcrumb-v2"
						key={crumb.key}
					>
						{crumb}
					</li>
				))}
			</ul>
			<JsonLdScript schema={crumbJSON} />
		</Fragment>
	);
}

BreadCrumbsV2.propTypes = {
	className: PropTypes.string,
	location: PropTypes.object,
	baseUrl: PropTypes.string,
	crumbs: PropTypes.node,
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	tertiary: PropTypes.bool,
	isCLPPage: PropTypes.bool,
};

export default BreadCrumbsV2;
